import { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Heart, Pencil, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";
import { toast } from "sonner";

export default function ProductCard({ product, isAdmin, onEdit, onDelete }) {
	const [isFavorite, setIsFavorite] = useState(false);
	const { addToCart } = useCart();

	const handleAddToCart = (e) => {
		e.preventDefault();
		addToCart(product, 1);
		toast.success(`${product.name} добавлен в корзину`);
	};

	const handleToggleFavorite = (e) => {
		e.preventDefault();
		setIsFavorite((prev) => !prev);
		toast.success(
			!isFavorite ? "Добавлено в избранное" : "Удалено из избранного"
		);
	};

	const handleAdminEdit = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (onEdit) onEdit();
	};

	const handleAdminDelete = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (onDelete) onDelete();
	};

	const isAvailable = product.availability === "есть";

	const getCardStyle = () => {
    if (product.category === 'Устрицы') {
      return 'from-sky-50 to-cyan-50';
    } else if (product.category === 'Живые морепродукты') {
      return 'from-blue-50 to-indigo-50';
    } else if (product.category === 'Замороженная рыба') {
      return 'from-slate-50 to-gray-100';
		}
    return 'from-purple-50 to-pink-50';
	};

	const getCategoryColor = () => {
    if (product.category === 'Устрицы') return 'bg-sky-100 text-sky-700';
    if (product.category === 'Живые морепродукты') return 'bg-blue-100 text-blue-700';
    if (product.category === 'Замороженная рыба') return 'bg-slate-100 text-slate-700';
    return 'bg-purple-100 text-purple-700';
	};

	return (
		<Link
			to={`/product/${product.id}`}
			className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
			data-testid={`product-card-${product.id}`}
		>
			<div className="relative overflow-hidden aspect-square">
				<div
					className={`absolute inset-0 bg-gradient-to-br ${getCardStyle()} opacity-20`}
				/>

				<img
					src={
						product.image_url ||
						"https://images.unsplash.com/photo-1565299624946-b28f40a0ae38"
					}
					alt={product.name}
					className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
					data-testid="product-image"
				/>

				{/* Оверлей «Нет в наличии» */}
				{!isAvailable && (
					<div className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-sm">
						<span className="bg-white text-gray-800 px-4 py-2 rounded-full font-semibold shadow-lg">
							{product.availability}
						</span>
					</div>
				)}

				{/* Кнопка избранного */}
				<button
					onClick={handleToggleFavorite}
					className="absolute top-4 right-4 w-10 h-10 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all shadow-lg"
					data-testid="favorite-button"
				>
					<Heart
						className={`w-5 h-5 transition-all ${
							isFavorite
								? "fill-rose-500 text-rose-500 scale-110"
								: "text-gray-600"
						}`}
					/>
				</button>

				{/* Бэйджи выгодно/Premium */}
				{isAvailable && product.price < 200 && (
					<div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
						Выгодно
					</div>
				)}
				{product.price > 1000 && (
					<div className="absolute top-4 left-4 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
						Premium
					</div>
				)}

				{/* АДМИН-КНОПКИ */}
				{isAdmin && (
					<div className="absolute bottom-4 left-4 flex gap-2 z-20">
						<Button
							size="icon"
							variant="outline"
							onClick={handleAdminEdit}
							title="Редактировать товар"
							className="bg-green-100/90 text-green-700 border border-green-300 hover:bg-green-500 hover:text-white shadow-md"
						>
							<Pencil className="w-4 h-4" />
						</Button>
						<Button
							size="icon"
							variant="destructive"
							onClick={handleAdminDelete}
							title="Удалить товар"
							className="bg-rose-600 hover:bg-rose-700 text-white shadow-md"
						>
							<Trash2 className="w-4 h-4" />
						</Button>
					</div>
				)}
			</div>

			<div className="p-6">
				<div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className={`text-xs font-semibold px-3 py-1 ${getCategoryColor()} rounded-full`}>
						{product.category}
					</span>
					{product.subcategory && (
						<span className="text-xs font-medium px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
							{product.subcategory}
						</span>
					)}
				</div>

        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 min-h-[3.5rem]" data-testid="product-name">
					{product.name}
				</h3>

				<div className="flex items-center text-sm text-gray-600 mb-3">
					<span className="mr-2">🌍</span>
					<span className="font-medium">{product.origin}</span>
				</div>

				{product.details && (
					<p className="text-xs text-gray-500 mb-4 line-clamp-1">
						{product.details}
					</p>
				)}

				<div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
					<div>
            <span className="text-2xl font-bold text-sky-600" data-testid="product-price">
              {product.price.toLocaleString('ru-RU')} ₽
						</span>
						{product.weight && (
							<p className="text-xs text-gray-500 mt-1">{product.weight}</p>
						)}
					</div>

					{isAvailable && (
						<Button
							onClick={handleAddToCart}
							data-testid="add-to-cart-button"
							className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl px-5 py-2.5 shadow-md hover:shadow-lg transition-all"
						>
							<ShoppingCart className="w-5 h-5" />
						</Button>
					)}
				</div>
			</div>
		</Link>
	);
}
